import { API_BASE_URL, LIMITED_LINK_KEY_HEADER } from './config';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  key: string;
  method?: 'GET' | 'POST';
  body?: unknown;
  signal?: AbortSignal;
}

interface ErrorBody {
  message?: string | string[];
}

export const NOT_MIDDLEWARE = 'That address did not answer with data from middleware.';

// Middleware answers 401 for a wrong key, 503 when limited links are not set up on the
// environment, and a Nest `message` (string or validation array) for everything it refuses.
export function messageFor(status: number, body: ErrorBody | null): string {
  if (status === 401) return 'The access key is wrong.';
  if (status === 403) return 'This page is for admins only.';
  if (body?.message) return Array.isArray(body.message) ? body.message.join(' ') : body.message;
  if (status === 503) return 'Limited links are not configured on this environment.';
  return `Something went wrong (${status}). Try again.`;
}

// Routes live under middleware's limited-tournament controller unless the path starts with
// `/`, which is then an absolute middleware path (the daily wheel history, same agent key).
export const urlFor = (path: string): string =>
  path.startsWith('/') ? `${API_BASE_URL}${path}` : `${API_BASE_URL}/api/tournaments/limited/${path}`;

export async function request<T>(path: string, options: RequestOptions): Promise<T> {
  const response = await fetch(urlFor(path), {
    method: options.method ?? 'GET',
    headers: {
      'Content-Type': 'application/json',
      [LIMITED_LINK_KEY_HEADER]: options.key,
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as ErrorBody | null;
    throw new ApiError(messageFor(response.status, body), response.status);
  }

  try {
    return (await response.json()) as T;
  } catch {
    // Middleware answers every route with JSON, so a 2xx that will not parse came from something
    // else on this origin — the dev server's index.html when VITE_API_BASE_URL is unset, a proxy's
    // own page. Refusing it here keeps callers from taking `null` for a payload.
    throw new ApiError(NOT_MIDDLEWARE, response.status);
  }
}

export const isUnauthorized = (error: unknown): boolean => error instanceof ApiError && error.status === 401;
